// frontend/src/components/ProcedureDetailModal.js
import React, { useState, useEffect } from "react";
import { supabase } from "../supabase";
import { writeClientAudit } from "../utils/auditClient";
import SavingOverlay from "./ui/SavingOverlay";

const statusOptions = [
  "未経験", "見学済み", "経験1回", "経験2回",
  "経験3回以上", "外から見守り", "独り立ち", "指導可",
];

const statusColors = {
  "未経験": "#e0e0e0",
  "見学済み": "#ffe0b2",
  "経験1回": "#ffcc80",
  "経験2回": "#ffb74d",
  "経験3回以上": "#ffa726",
  "外から見守り": "#66bb6a",
  "独り立ち": "#26a69a",
  "指導可": "#00897b"
};

const ProcedureDetailModal = ({ 
  isOpen, 
  onClose, 
  procedure, 
  staffId, 
  organizationId,
  currentUser,
  onSave 
}) => {
  const [activeTab, setActiveTab] = useState("evaluation");
  const [skillData, setSkillData] = useState({});
  const [checklists, setChecklists] = useState({});
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [staffDisplayName, setStaffDisplayName] = useState("");

  useEffect(() => {
    if (isOpen && procedure) {
      loadData();
    }
  }, [isOpen, procedure]);

  const loadData = async () => {
    setLoading(true);
    try {
      // ログイン中ユーザーの名前を取得（staffIdではなく現在のセッションユーザー）
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser) {
        const { data: loginStaff } = await supabase
          .from('staff')
          .select('last_name, first_name')
          .eq('organization_id', organizationId)
          .or(`email.eq.${authUser.email},auth_uid.eq.${authUser.id}`)
          .single();
        if (loginStaff) {
          setStaffDisplayName(`${loginStaff.last_name} ${loginStaff.first_name}`.trim());
        }
      }
      const { data: skillRecords } = await supabase
        .from('skill_records')
        .select('*')
        .eq('user_id', staffId)
        .eq('procedure_id', procedure.id)
        .eq('organization_id', organizationId);

      const skillMap = {};
      (skillRecords || []).forEach(record => {
        const key = record.label ? `${record.role}_${record.label}` : record.role;
        skillMap[key] = { level: record.level, id: record.id, history: record.evaluation_history || [] };
      });
      setSkillData(skillMap);

      const { data: checklistMaster } = await supabase
        .from('procedure_checklists')
        .select('*')
        .eq('procedure_id', procedure.id)
        .eq('organization_id', organizationId);

      const checklistMap = {};
      (checklistMaster || []).forEach(item => {
        checklistMap[item.role] = item.checklist_items || [];
      });
      setChecklists(checklistMap);

      const { data: progressData } = await supabase
        .from('checklist_progress')
        .select('*')
        .eq('user_id', staffId)
        .eq('procedure_id', procedure.id)
        .eq('organization_id', organizationId);

      const progressMap = {};
      (progressData || []).forEach(item => {
        progressMap[item.role] = item.completed_items || [];
      });
      setProgress(progressMap);

    } catch (error) {
      console.error("❌ データ取得エラー:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSkillChange = async (role, label, newLevel) => {
    const key = label ? `${role}_${label}` : role;
    const oldData = skillData[key] || {};

    // 新しい履歴エントリ
    const historyEntry = {
      level: newLevel,
      evaluated_at: new Date().toISOString().split('T')[0],
      evaluated_by: staffDisplayName || currentUser?.email || "不明"
    };
  
    try {
      setSaving(true);
  
      if (oldData.id) {
        // 既存の履歴に追記
        const existingHistory = oldData.history || [];
        const newHistory = [...existingHistory, historyEntry];
        const { error } = await supabase
          .from('skill_records')
          .update({ level: newLevel, evaluation_history: newHistory })
          .eq('id', oldData.id);
        if (error) throw error;
        setSkillData(prev => ({ ...prev, [key]: { ...oldData, level: newLevel, history: newHistory } }));
        await writeClientAudit({
          organizationId,
          actionType: 'update',
          targetTable: 'skill_records',
          targetId: oldData.id,
          targetSummary: `経験録更新: ${procedure.name || procedure.id} / ${role}${label ? ` (${label})` : ''} → ${newLevel}`,
          changes: { user_id: staffId, procedure_id: procedure.id, role, label, newLevel },
          success: true,
        });
      } else {
        // 新規
        const record = {
          user_id: staffId,
          procedure_id: procedure.id,
          organization_id: organizationId,
          role: role,
          level: newLevel,
          evaluation_history: [historyEntry]
        };
        if (label) record.label = label;

        const { data, error } = await supabase
          .from('skill_records')
          .insert(record)
          .select()
          .single();
        if (error) throw error;
        setSkillData(prev => ({ ...prev, [key]: { level: newLevel, id: data.id, history: [historyEntry] } }));
        await writeClientAudit({
          organizationId,
          actionType: 'create',
          targetTable: 'skill_records',
          targetId: data?.id,
          targetSummary: `経験録新規作成: ${procedure.name || procedure.id} / ${role}${label ? ` (${label})` : ''} = ${newLevel}`,
          changes: { user_id: staffId, procedure_id: procedure.id, role, label, newLevel },
          success: true,
        });
      }
    } catch (error) {
      console.error("❌ スキル保存エラー:", error);
      await writeClientAudit({
        organizationId,
        actionType: 'update',
        targetTable: 'skill_records',
        targetSummary: `経験録保存失敗: ${procedure?.name || procedure?.id} / ${role}`,
        success: false,
        errorMessage: error.message,
      });
      alert("保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const toggleChecklistItem = async (role, itemId) => {
    const currentProgress = progress[role] || [];
    const isCompleted = currentProgress.some(item => item.item_id === itemId);
  
    let newProgress;
    if (isCompleted) {
      newProgress = currentProgress.filter(item => item.item_id !== itemId);
    } else {
      newProgress = [
        ...currentProgress,
        {
          item_id: itemId,
          completed_at: new Date().toISOString().split('T')[0],
          completed_by: staffDisplayName || currentUser?.name || currentUser?.email || "不明"
        }
      ];
    }
  
    try {
      setSaving(true);
  
      const { data: existing } = await supabase
        .from('checklist_progress')
        .select('id')
        .eq('user_id', staffId)
        .eq('procedure_id', procedure.id)
        .eq('organization_id', organizationId)
        .eq('role', role)
        .maybeSingle();
  
      if (existing) {
        await supabase
          .from('checklist_progress')
          .update({ completed_items: newProgress, last_updated: new Date().toISOString() })
          .eq('id', existing.id);
      } else {
        await supabase
          .from('checklist_progress')
          .insert({
            user_id: staffId,
            procedure_id: procedure.id,
            organization_id: organizationId,
            role: role,
            completed_items: newProgress
          });
      }
  
      setProgress(prev => ({ ...prev, [role]: newProgress }));
    } catch (error) {
      console.error("❌ チェックリスト保存エラー:", error);
      alert("保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const calculateProgress = (role) => {
    const checklist = checklists[role] || [];
    const totalItems = checklist.reduce((sum, cat) => sum + cat.items.length, 0);
    const completedItems = (progress[role] || []).length;
    if (totalItems === 0) return 0;
    return Math.round((completedItems / totalItems) * 100);
  };

  if (!isOpen || !procedure) return null;

  const roles = procedure.requiredRoles && procedure.requiredRoles.length > 0
    ? procedure.requiredRoles
    : [
        { type: "scrub", label: null },
        { type: "circulating", label: null }
      ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
      <SavingOverlay visible={saving} message="経験録を保存中..." />
      <div className="flex items-start justify-center" style={{ minHeight: '100%', padding: '60px 16px 80px' }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl flex flex-col">

        {/* ヘッダー（固定） */}
        <div className="bg-teal-600 text-white p-4 sm:p-6 flex justify-between items-center flex-shrink-0 rounded-t-xl">
          <div className="min-w-0 flex-1 mr-3">
            <h2 className="text-xl sm:text-2xl font-bold truncate">{procedure.name}</h2>
            <p className="text-teal-100 text-sm mt-1">{procedure.departmentName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-teal-700 rounded-full p-2 transition flex-shrink-0"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* タブナビゲーション（固定） */}
        <div className="border-b border-gray-200 overflow-x-auto flex-shrink-0">
          <div className="flex">
            <button
              onClick={() => setActiveTab("evaluation")}
              className={`px-4 sm:px-6 py-3 font-medium transition whitespace-nowrap text-sm sm:text-base ${
                activeTab === "evaluation"
                  ? "border-b-2 border-teal-600 text-teal-600 bg-teal-50"
                  : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
              }`}
            >
              👤 他者評価
            </button>
            {roles.map((role, idx) => {
              const roleLabel = role.label || (role.type === "scrub" ? "器械出し" : "外回り");
              const hasChecklist = checklists[role.type] && checklists[role.type].length > 0;
              if (!hasChecklist) return null;

              return (
                <button
                  key={idx}
                  onClick={() => setActiveTab(`checklist_${role.type}_${role.label || 'default'}`)}
                  className={`px-4 sm:px-6 py-3 font-medium transition whitespace-nowrap text-sm sm:text-base ${
                    activeTab === `checklist_${role.type}_${role.label || 'default'}`
                      ? "border-b-2 border-teal-600 text-teal-600 bg-teal-50"
                      : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                  }`}
                >
                  📋 チェックリスト（{roleLabel}）
                </button>
              );
            })}
          </div>
        </div>

        {/* コンテンツエリア */}
        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">読み込み中...</p>
            </div>
          ) : activeTab === "evaluation" ? (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">スキルレベル評価</h3>
              {roles.map((role, idx) => {
                const key = role.label ? `${role.type}_${role.label}` : role.type;
                const currentSkill = skillData[key] || {};
                const currentLevel = currentSkill.level || "未経験";
                const roleLabel = role.label || (role.type === "scrub" ? "器械出し" : "外回り");

                return (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-700">{roleLabel}</h4>
                      <span 
                        className="px-3 py-1 rounded-full text-sm font-medium"
                        style={{ 
                          backgroundColor: statusColors[currentLevel],
                          color: ["未経験", "見学済み", "経験1回", "経験2回"].includes(currentLevel) ? "black" : "white"
                        }}
                      >
                        {currentLevel}
                      </span>
                    </div>
                    <select
                      value={currentLevel}
                      onChange={(e) => handleSkillChange(role.type, role.label, e.target.value)}
                      disabled={saving}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 mb-3"
                    >
                      {statusOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>

                    {/* 評価履歴 */}
                    {currentSkill.history && currentSkill.history.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-medium text-gray-500 mb-1">📋 評価履歴</p>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {[...currentSkill.history].reverse().map((h, i) => (
                            <div key={i} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1">
                              <span
                                className="px-2 py-0.5 rounded-full font-medium"
                                style={{
                                  backgroundColor: statusColors[h.level],
                                  color: ["未経験", "見学済み", "経験1回", "経験2回"].includes(h.level) ? "black" : "white"
                                }}
                              >
                                {h.level}
                              </span>
                              <span className="text-gray-500">{h.evaluated_at} · {h.evaluated_by}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            (() => {
              const parts = activeTab.split('_');
              const roleType = parts[1];
              const checklist = checklists[roleType] || [];
              const progressData = progress[roleType] || [];
              const progressPercent = calculateProgress(roleType);

              if (checklist.length === 0) {
                return (
                  <div className="text-center py-12 text-gray-500">
                    <p>このチェックリストはまだ作成されていません。</p>
                    <p className="text-sm mt-2">チェックリスト管理画面から追加してください。</p>
                  </div>
                );
              }

              return (
                <div className="space-y-6">
                  <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-teal-800">進捗状況</h3>
                      <span className="text-2xl font-bold text-teal-600">{progressPercent}%</span>
                    </div>
                    <div className="w-full bg-teal-200 rounded-full h-3">
                      <div 
                        className="bg-teal-600 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${progressPercent}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-teal-700 mt-2">
                      {progressData.length} / {checklist.reduce((sum, cat) => sum + cat.items.length, 0)} 項目完了
                    </p>
                  </div>

                  {checklist.map((category, catIdx) => (
                    <div key={catIdx} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-100 px-4 py-3">
                        <h4 className="font-semibold text-gray-800">【{category.category}】</h4>
                      </div>
                      <div className="p-4 space-y-2">
                        {category.items.map((item) => {
                          const completedItem = progressData.find(p => p.item_id === item.id);
                          const isCompleted = !!completedItem;

                          return (
                            <div key={item.id} className="flex items-start space-x-3 p-2 hover:bg-gray-50 rounded">
                              <input
                                type="checkbox"
                                checked={isCompleted}
                                onChange={() => toggleChecklistItem(roleType, item.id)}
                                disabled={saving}
                                className="mt-1 h-5 w-5 text-teal-600 rounded focus:ring-teal-500 cursor-pointer flex-shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <label className={`text-sm cursor-pointer ${isCompleted ? 'text-gray-500 line-through' : 'text-gray-800'}`}>
                                  {item.text}
                                </label>
                                {isCompleted && completedItem && (
                                  <div className="text-xs text-green-600 mt-1">
                                    ✓ {completedItem.completed_at} by {completedItem.completed_by}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()
          )}
        </div>

        {/* フッター（固定） */}
        <div className="border-t border-gray-200 p-4 bg-gray-50 flex justify-end flex-shrink-0 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
          >
            閉じる
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};

export default ProcedureDetailModal;